import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'next-i18next/pages';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';

import { useCookiesConsentManagerContext } from '@pflegia/ui/contexts/cookies-consent-manager.context';
import Modal from '@pflegia/ui/shared/modal/Modal';
import { useToast } from '@pflegia/ui/shared/toaster/ToastProvider';

import useUpdatePflegiaFriendsStats from '@hooks/useUpdatePflegiaFriendsStats';

import { FUNNEL } from '@shared/application/constants';
import { VALIDATE_REFERRAL_CODE } from '@shared/graphql/operations/validate-referral-code';

import PflegiaFriendsContent from './PflegiaFriendsContent.component';
import { referrerCodeStorage } from './referrer-code.storage';

const PflegiaFriends = () => {
  const toast = useToast();

  const { t } = useTranslation('landingPage', { keyPrefix: 'pflegiaFriends' });

  const router = useRouter();
  const referrerCode = router.query?.referral as string;
  const source = router.query?.source as string;

  const segmentContext = useCookiesConsentManagerContext();
  const { data, error } = useQuery(VALIDATE_REFERRAL_CODE, {
    variables: { referrenceCode: referrerCode },
    skip: !segmentContext?.ready || !referrerCode,
  });
  useEffect(() => {
    if (!error) return;
    toast?.error(t('error'));
  }, [error, t, toast]);
  const [show, setShow] = useState<boolean>(false);

  useUpdatePflegiaFriendsStats({ referrerCode, isReferrerValid: Boolean(data?.referrer), source });

  useEffect(() => {
    const hasSetCookiePreferences = Object.keys(segmentContext?.cookiesSettings || {}).length > 0;
    setShow(Boolean(data?.referrer) && segmentContext?.ready && hasSetCookiePreferences);
  }, [data?.referrer, segmentContext?.ready, segmentContext?.cookiesSettings]);

  const handleSubmit = useCallback(() => {
    setShow(false);
    referrerCodeStorage.setReferrerCode(referrerCode);
    router.push(FUNNEL);
  }, [referrerCode, router]);

  const handleClose = useCallback(() => {
    setShow(false);
    referrerCodeStorage.setReferrerCode(referrerCode);
    router.push('/');
  }, [referrerCode, router]);

  return (
    <>
      {show && data?.referrer && (
        <Modal size='compact' className='pb-0 lg:pl-0' onClose={handleClose} scrollable>
          <PflegiaFriendsContent referrer={data.referrer} onSubmit={handleSubmit} />
        </Modal>
      )}
    </>
  );
};

export default PflegiaFriends;
